import axios from "axios";
import queryString from "query-string";

/**
 * Client for the Agent Arbo BFF mounted at /api/agent/ on arbo-api.
 *
 * The SPA talks to this surface only. It never calls data-api directly, and never the
 * ported runtime's platform API -- arbo-api is what runs the agent, holds the corpus,
 * and resolves a turn's citations and tariff ids into links this app can follow.
 *
 * Two house quirks are handled here:
 *  1. The shared axios interceptor rejects with `error.response.data`, so callers lose
 *     the status code. Every call goes through `request()` below, which throws an
 *     `AgentApiError` carrying `status`, and returns the whole response so callers can
 *     tell 202 (still running) from 200 (settled).
 *  2. DRF reads repeated query keys with `getlist`, so arrays must serialise as
 *     `topics=a&topics=b`, not axios' default `topics[]=a`.
 *
 * This is Ask's half of the BFF: chat, sessions, capabilities and feedback. The surface also
 * exposes a search / suggest / narrative family, which only the ArView Insights and PI Alerts
 * screens call; those screens are not on this branch, so those methods are not declared here.
 */

export class AgentApiError extends Error {
    constructor(status, data) {
        super(data?.detail || `Agent request failed (${status})`);
        this.name = "AgentApiError";
        this.status = status;
        this.data = data;
    }
}

export const paramsSerializer = (params) => queryString.stringify(params, { arrayFormat: "none", skipNull: true });

/**
 * `validateStatus` lets every status except 401 resolve, so we can inspect it. 401 still
 * rejects into the shared interceptor, which refreshes the token and retries once.
 */
export const request = (config) =>
    axios({ ...config, paramsSerializer, validateStatus: (status) => status !== 401 })
        .then((response) => {
            if (response.status >= 400) {
                throw new AgentApiError(response.status, response.data);
            }
            return response;
        })
        .catch((error) => {
            if (error instanceof AgentApiError) throw error;
            throw new AgentApiError(error?.response?.status ?? 0, error?.response?.data ?? { detail: String(error) });
        });

/** What this reader is allowed to do; used to hide Ask entirely when they cannot. */
const getCapabilities = () => request({ method: "get", url: "/agent/" }).then((r) => r.data);

/**
 * The signed-in reader's Ask threads, newest first. `cursor` is opaque and comes from a
 * previous response's `next_cursor`; its absence means there is no next page.
 *
 * Not deployed on every arbo-api: where the route is absent this answers 404, which
 * `useAskSessions` reads as "show this device's list" rather than as a failure.
 */
const sessionsList = (params) => request({ method: "get", url: "/agent/sessions/", params }).then((r) => r.data);

/**
 * Rename or pin one of the reader's threads. Answers with the updated row, in the list's shape.
 */
const sessionUpdate = (sessionId, data) =>
    request({ method: "patch", url: `/agent/sessions/${sessionId}/`, data }).then((r) => r.data);

/**
 * Take a thread out of the reader's list. The server hides it rather than deleting it -- the
 * turns and their feedback are kept -- but to this reader it is gone: it leaves the list and
 * search, and opening it again answers 404.
 */
const sessionDelete = (sessionId) => request({ method: "delete", url: `/agent/sessions/${sessionId}/` });

const chatSend = (data) => request({ method: "post", url: "/agent/chat/", data });

const chatPoll = (sessionId, params) => request({ method: "get", url: `/agent/chat/${sessionId}/`, params });

const feedbackPost = (data) => request({ method: "post", url: "/agent/feedback/", data }).then((r) => r.data);

const feedbackGet = (params) => request({ method: "get", url: "/agent/feedback/", params }).then((r) => r.data);

const ApiAgent = {
    getCapabilities,
    sessionsList,
    sessionUpdate,
    sessionDelete,
    chatSend,
    chatPoll,
    feedbackPost,
    feedbackGet,
};

export default ApiAgent;
